import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveChat } from '@/contexts/ActiveChatContext';
import { supabase } from '@/integrations/supabase/client';
import type { ChatMessage, ValidationResult } from '@/types/chat';

// Re-export the context hook as useChats for backward compatibility
import { useChatsContext } from '@/contexts/ChatsContext';

export function useChats() {
  return useChatsContext();
}

export function useChatMessages(chatId: string | null) {
  const { user } = useAuth();
  const { setActiveChatId } = useActiveChat();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Register this chat as active when mounted
  useEffect(() => {
    if (chatId) {
      setActiveChatId(chatId);
    }
    return () => {
      setActiveChatId(null);
    };
  }, [chatId, setActiveChatId]);

  const fetchMessages = useCallback(async () => {
    if (!user || !chatId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages((data as ChatMessage[]) || []);

    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [user, chatId]);

  // Mark messages as read - separate from fetch to avoid race conditions
  const markMessagesAsRead = useCallback(async () => {
    if (!user || !chatId) return;
    
    await supabase
      .from('chat_messages')
      .update({ is_read: true })
      .eq('chat_id', chatId)
      .neq('sender_id', user.id)
      .eq('is_read', false);
  }, [user, chatId]);

  // Mark messages as read after a short delay
  useEffect(() => {
    if (!user || !chatId || loading) return;
    
    const timer = setTimeout(() => {
      markMessagesAsRead();
    }, 300);

    return () => clearTimeout(timer);
  }, [user, chatId, loading, markMessagesAsRead]);

  // Validate message content via edge function
  const validateMessage = async (content: string): Promise<ValidationResult> => {
    try {
      const response = await supabase.functions.invoke('validate-chat-message', {
        body: { content },
      });

      if (response.error) {
        return { valid: false, reason: 'Failed to validate message' };
      }

      return response.data as ValidationResult;
    } catch (err) {
      console.error('Error validating message:', err);
      return { valid: false, reason: 'Failed to validate message' };
    }
  };

  // Send a message
  const sendMessage = async (content: string): Promise<{ success: boolean; error?: string }> => {
    if (!user || !chatId) {
      return { success: false, error: 'Not authenticated' };
    }

    setSending(true);
    try {
      // First validate the message
      const validation = await validateMessage(content);
      if (!validation.valid) {
        return { success: false, error: validation.reason };
      }

      // Insert the message
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          chat_id: chatId,
          sender_id: user.id,
          content: content.trim(),
        });

      if (error) {
        console.error('Send message error:', error);
        // Check for specific error conditions
        if (error.message.includes('can_receive_chat_messages')) {
          return { success: false, error: 'The other user has archived or deleted this chat.' };
        }
        throw error;
      }

      return { success: true };
    } catch (err) {
      console.error('Error sending message:', err);
      return { success: false, error: 'Failed to send message' };
    } finally {
      setSending(false);
    }
  };

  // Subscribe to realtime updates for this chat
  useEffect(() => {
    if (!user || !chatId) return;

    const channel = supabase
      .channel(`chat-messages-${chatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_id=eq.${chatId}`,
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage;
          setMessages(prev => [...prev, newMessage]);
          
          // Mark as read if not from current user
          if (newMessage.sender_id !== user.id) {
            supabase
              .from('chat_messages')
              .update({ is_read: true })
              .eq('id', newMessage.id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, chatId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    loading,
    sending,
    sendMessage,
    validateMessage,
    refetch: fetchMessages,
  };
}

export function useStartChat() {
  const { user } = useAuth();
  const [starting, setStarting] = useState(false);

  const startChat = async (notificationId: string): Promise<{ success: boolean; chatId?: string; error?: string }> => {
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    setStarting(true);
    try {
      // Get the notification to determine participants
      const { data: notification, error: notifError } = await supabase
        .from('notifications')
        .select('from_user_id, to_user_id')
        .eq('id', notificationId)
        .single();

      if (notifError || !notification) {
        return { success: false, error: 'Notification not found' };
      }

      // Verify user is the recipient of the notification
      if (notification.to_user_id !== user.id) {
        return { success: false, error: 'You can only start a chat from notifications you received' };
      }

      // Check if chat already exists
      const { data: existingChat } = await supabase
        .from('chats')
        .select('id')
        .eq('notification_id', notificationId)
        .maybeSingle();

      if (existingChat) {
        return { success: true, chatId: existingChat.id };
      }

      // Check if other user can receive chats (membership tier)
      const { data: canChat } = await supabase
        .rpc('user_can_chat', { target_user_id: notification.from_user_id });

      if (!canChat) {
        return { 
          success: false, 
          error: 'This user cannot receive chat messages due to their membership tier.' 
        };
      }

      // Create the chat
      const { data: newChat, error: chatError } = await supabase
        .from('chats')
        .insert({
          notification_id: notificationId,
          started_by: user.id,
          participant_1: notification.from_user_id,
          participant_2: notification.to_user_id,
        })
        .select('id')
        .single();

      if (chatError) {
        console.error('Chat creation error:', chatError);
        // Check for specific error conditions
        if (chatError.message.includes('blocked')) {
          return { success: false, error: 'This user has blocked you.' };
        }
        throw chatError;
      }

      return { success: true, chatId: newChat.id };
    } catch (err) {
      console.error('Error starting chat:', err);
      return { success: false, error: 'Failed to start chat. Please try again.' };
    } finally {
      setStarting(false);
    }
  };

  return { startChat, starting };
}
